import { memberStorage } from 'utils';
import { selectProduct } from 'logic/product';
import { setNavType } from 'logic/theme';

import {
  changeSubscription,
  selectMember,
  selectSubscription,
} from 'logic/member';

import {
  fetchWorkout,
  updateWorkoutStep,
  previousWorkoutStep,
  nextWorkoutStep,
  selectWorkout,
  selectWorkoutSteps,
  selectExercises,
  selectCurrentStep,
  selectPreviousStep,
  selectNextStep,
} from 'logic/workout';

export const WorkoutPlayer = {
  template: `
    <div class="workout-player-container">
      <workout-player-header
        is-complete="$ctrl.player.completed"
        is-finished="$ctrl.finished"
        on-click-finish="$ctrl.finished = true"
        on-click-logo="$ctrl.changeSubscription($ctrl.subscription)">
      </workout-player-header>
      <div ng-hide="$ctrl.finished">
        <workout-player-progress-bar
          steps="$ctrl.steps"
          current-step="$ctrl.currentStep"
          current-step-index="$ctrl.currentStepIndex"
          time="$ctrl.player.elapsedTime">
        </workout-player-progress-bar>
        <a class="workout-player-exercise-name"
          href="#"
          ng-click="$ctrl.toggleDetails()">
          {{ $ctrl.currentStep.exercise.name }}
          <i class="fa fa-info-circle"></i>
        </a>
        <div class="workout-player-video-container">
          <div class="workout-player-video" ng-if="$ctrl.currentStep.exercise.video">
            <workout-player-video
              countdown-complete="$ctrl.player.started"
              video-scale="{{ $ctrl.currentStep.exerciseVideo.scale }}"
              video-id="{{ $ctrl.currentStep.video.brightcoveId }}"
              autoplay="true"
              playing="$ctrl.player.playing"
              waiting="$ctrl.player.waiting"
              on-play="$ctrl.play()"
              on-wait="$ctrl.player.waiting = true"
              on-resume="$ctrl.player.waiting = false">
            </workout-player-video>
          </div>
          <div class="side-panels-container">
            <workout-player-context-panel
              workout="$ctrl.workout"
              steps="$ctrl.steps"
              current-step="$ctrl.currentStep"
              current-step-index="$ctrl.currentStepIndex"
              exercises="$ctrl.exercises"
              on-click="$ctrl.toggleDetails()">
            </workout-player-context-panel>
            <div class="workout-player-current-exercise-panel"
              ng-show="$ctrl.currentStep"
              ng-class="{
                'final-blast': $ctrl.currentStep.group.name.toLowerCase() === 'final blast',
                'step-complete': $ctrl.player.completed
              }">
              <workout-player-countdown
                countdown="$ctrl.player.countdown"
                playing="$ctrl.player.playing"
                waiting="$ctrl.player.waiting">
              </workout-player-countdown>
              <workout-player-timer
                step="$ctrl.currentStep"
                elapsed-time="$ctrl.player.elapsedTime"
                countdown="$ctrl.player.countdown"
                on-started="$ctrl.started()"
                on-completed="$ctrl.completed()">
              </workout-player-timer>
            </div>
          </div>
        </div>
        <div class="workout-player-footer d-flex flex-column flex-md-row"
          ng-class="{
            'final-blast': $ctrl.currentStep.group.name.toLowerCase() === 'final blast',
            'step-complete': $ctrl.player.completed
          }">
          <div class="workout-player-caption py-2 py-md-0 flex-last flex-md-first"
            ng-class="{ 'step-complete': $ctrl.player.completed }">
            <img
              ng-src="{{ $ctrl.nextStep.exercise.icon.thumb2x | assetSrc }}"
              ng-if="$ctrl.nextStep.exercise.icon">
            <span>
              <strong class="text-uppercase">Next:</strong>
              {{ $ctrl.nextStep.exercise.name || 'Workout Complete' }}
            </span>
          </div>
          <div class="workout-player-controls d-flex ml-md-auto">
            <workout-player-controls
              playing="$ctrl.player.playing"
              completed="$ctrl.player.completed"
              continuous="$ctrl.continuous"
              previous-step="$ctrl.previousStep"
              next-step="$ctrl.nextStep"
              on-toggle="$ctrl.toggle()"
              on-toggle-continuous="$ctrl.continuous = !$ctrl.continuous"
              on-restart="$ctrl.restartStep()"
              on-back="$ctrl.goPrevious()"
              on-next="$ctrl.goNext()">
            </workout-player-controls>
          </div>
        </div>
        <workout-player-instructions
          step="$ctrl.currentStep"
          show="$ctrl.showDetails"
          on-close="$ctrl.showDetails = false">
        </workout-player-instructions>
      </div>
      <div ng-show="$ctrl.finished">
        <workout-player-finished
          workout="$ctrl.workout"
          member="$ctrl.member"
          voome-product-id="$ctrl.voomeProductId">
        </workout-player-finished>
      </div>
      <workout-player-sleeper
        playing="$ctrl.player.playing"
        on-play="$ctrl.play()"
        on-pause="$ctrl.pause()">
      </workout-player-sleeper>
    </div>`,

  controller($ngRedux, $interval, $timeout) {
    'ngInject';

    let ticker;
    let time;
    let timePrev;

    const initialPlayerState = {
      started: false,
      completed: false,
      elapsedTime: 0,
      countdown: null,
      waiting: true,
      autoplay: false,
    };

    return {
      $onInit() {
        const connect = $ngRedux.connect(
          this.mapState.bind(this),
          this.mapDispatch()
        );

        this.disconnect = connect(this);
        this.continuous = true;
        this.autoplay = true;

        this.initPlayer({
          playing: false,
          startPlaying: true,
        });

        this.setNavType('none');
        this.fetchWorkout(this.exercisePlanId, this.id, this.itemId || 0);
      },

      $onDestroy() {
        this.setNavType('main');
        this.updateWorkoutStep(0);
        this.disconnect();
      },

      mapState(state) {
        const { exercisePlanId, id, itemId } = state.router.currentParams;

        const product = selectProduct(state.product);
        const currentStep = selectCurrentStep(state.workout);
        const currentStepIndex = state.workout.currentStepIndex;

        // TODO: look into removing this, dont want it in mapState
        this.updatePlayer(currentStep, currentStepIndex);

        return {
          id,
          exercisePlanId,
          itemId,
          currentStep,
          currentStepIndex,
          exercises: selectExercises(state.workout),
          member: selectMember(state.member),
          nextStep: selectNextStep(state.workout),
          previousStep: selectPreviousStep(state.workout),
          steps: selectWorkoutSteps(state.workout),
          subscription: selectSubscription(state.member),
          voomeProductId: product && product.id,
          workout: selectWorkout(state.workout),
        };
      },

      mapDispatch() {
        return {
          changeSubscription,
          fetchWorkout,
          nextWorkoutStep,
          previousWorkoutStep,
          setNavType,
          updateWorkoutStep,
        };
      },

      initPlayer(options = {}) {
        this.player = {
          ...initialPlayerState,
          ...options,
        };
      },

      // TODO: check with DCAL if this can moved to $onInit.
      updatePlayer(step, stepIndex) {
        if (!step || !this.player) {
          return;
        }

        // If the step index has changed,
        // or we've flagged the player to restart
        if (stepIndex !== this.player.stepIndex || this.player.restart) {
          if (this.player.startPlaying) {
            this.playDelay();
          }

          this.initPlayer({
            countdown: step.restSeconds,
            playing: this.player.playing,
            stepIndex,
          });

          // Sync the ticker with the player
          this.sync();
        }
      },

      start() {
        this.clock();
        ticker = $interval(() => this.tick(), 1000);
      },

      stop() {
        if (ticker) {
          $interval.cancel(ticker);
          ticker = null;
        }
      },

      sync() {
        if (ticker) {
          this.stop();
          this.start();
        }
      },

      tick() {
        const timePassed = this.clock();

        if (timePassed > 0) {
          if (this.player.countdown > 0) {
            this.player.countdown = this.player.countdown - timePassed;
          } else {
            this.player.elapsedTime = this.player.elapsedTime + timePassed;
          }
        }
      },

      // Sets time to now.
      // Returns time passed since last clock.
      clock() {
        timePrev = time;
        time = Math.round(Date.now() / 1000);
        return timePrev ? time - timePrev : 0;
      },

      play() {
        this.stop();
        this.start();
        this.player.playing = true;
      },

      pause() {
        this.stop();
        this.player.playing = false;
      },

      toggle() {
        if (this.player.playing) {
          this.pause();
        } else {
          this.play();
        }

        if (this.player.playing && this.player.countdown > 0) {
          this.player.countdown = 5;
        }
      },

      playDelay(delay = 300) {
        $timeout(() => this.play(), delay);
      },

      started() {
        this.player.started = true;
      },

      completed() {
        this.player.completed = true;
        this.pause();

        if (this.continuous) {
          this.goNext();
          this.playDelay();
        }
      },

      finish() {
        this.finished = true;
      },

      goPrevious() {
        this.previousWorkoutStep(this.currentStepIndex);
      },

      goNext() {
        if (this.nextStep) {
          this.nextWorkoutStep(this.currentStepIndex);
        } else {
          this.finish();
        }
      },

      restartStep() {
        this.player.restart = true;
        this.updateWorkoutStep(this.currentStepIndex);
        this.play();
      },

      toggleDetails() {
        this.showDetails = !this.showDetails;

        if (this.showDetails) {
          this.pause();
        }
      },
    };
  },
};



// WEBPACK FOOTER //
// ./src/containers/WorkoutPlayer.js